import { create } from 'zustand'
import { STANDAARD_SCENE, metProfiel } from './config'
import type { Profiel, SceneConfig } from './config'

type Groep = keyof SceneConfig

/**
 * Wie het profiel koos. `auto`: de meting bij het opstarten (Prestatie.tsx);
 * `handmatig`: iemand in het beheerderspaneel — dan meet de kiosk niet meer.
 */
export type ProfielBron = 'auto' | 'handmatig'

/**
 * De beheerdersinstellingen van deze machine, onder één sleutel in
 * localStorage (PLAN.md §3, §4.10: de sessie-reset wist hem nooit). Elke
 * toegang in try/catch: een kioskprofiel zonder opslag moet gewoon draaien.
 */
const OPSLAG = 'motrac-kiosk-beheer'

function leesBeheer(): { profiel: Profiel; profielBron: ProfielBron } {
  try {
    const ruw = JSON.parse(localStorage.getItem(OPSLAG) ?? '{}')
    return {
      profiel: ruw.profiel === 'licht' ? 'licht' : 'vol',
      profielBron: ruw.profielBron === 'handmatig' ? 'handmatig' : 'auto',
    }
  } catch {
    return { profiel: 'vol', profielBron: 'auto' }
  }
}

function schrijfBeheer(waarden: { profiel: Profiel; profielBron: ProfielBron }) {
  try {
    const ruw = JSON.parse(localStorage.getItem(OPSLAG) ?? '{}')
    localStorage.setItem(OPSLAG, JSON.stringify({ ...ruw, ...waarden }))
  } catch {
    // Geen opslag: het profiel geldt dan alleen voor deze sessie.
  }
}

const BEGIN = leesBeheer()

interface SceneState {
  scene: SceneConfig
  paneelOpen: boolean
  profiel: Profiel
  profielBron: ProfielBron
  /** Telt op als de kiosk opnieuw moet meten (keuze "Automatisch" in het paneel). */
  meetRonde: number
  /** Gemeten beelden per seconde bij de laatste meting, of null. */
  gemetenFps: number | null

  /** Zet één waarde binnen een groep, bv. zet('licht', 'zonSterkte', 1.4). */
  zet: <G extends Groep, K extends keyof SceneConfig[G]>(
    groep: G,
    sleutel: K,
    waarde: SceneConfig[G][K],
  ) => void

  herstel: () => void
  herstelGroep: (groep: Groep) => void
  zetProfiel: (profiel: Profiel, bron: ProfielBron) => void
  /** Terug naar automatisch: vol profiel, en de meting opnieuw. */
  meetOpnieuw: () => void
  meldFps: (fps: number) => void
  zetPaneel: (open: boolean) => void
  /** Het hele document als JSON, om te exporteren en te committen. */
  exporteer: () => string
}

export const useScene = create<SceneState>((set, get) => ({
  scene: metProfiel(STANDAARD_SCENE, BEGIN.profiel),
  paneelOpen: false,
  profiel: BEGIN.profiel,
  profielBron: BEGIN.profielBron,
  meetRonde: 0,
  gemetenFps: null,

  zet: (groep, sleutel, waarde) =>
    set((s) => ({
      scene: { ...s.scene, [groep]: { ...s.scene[groep], [sleutel]: waarde } },
    })),

  // Herstellen gaat naar de standaard van het huidige profiel, niet naar de
  // volle scène: anders zet een beheerder op een zwakke pc met één knop alles
  // weer aan wat het lichte profiel uitzette.
  herstel: () => set((s) => ({ scene: metProfiel(STANDAARD_SCENE, s.profiel) })),
  herstelGroep: (groep) =>
    set((s) => ({ scene: { ...s.scene, [groep]: metProfiel(STANDAARD_SCENE, s.profiel)[groep] } })),

  zetProfiel: (profiel, profielBron) => {
    schrijfBeheer({ profiel, profielBron })
    set((s) => ({ profiel, profielBron, scene: metProfiel(s.scene, profiel) }))
  },
  meetOpnieuw: () => {
    schrijfBeheer({ profiel: 'vol', profielBron: 'auto' })
    set((s) => ({
      profiel: 'vol', profielBron: 'auto', meetRonde: s.meetRonde + 1, gemetenFps: null,
      scene: metProfiel(s.scene, 'vol'),
    }))
  },
  meldFps: (gemetenFps) => set({ gemetenFps }),

  zetPaneel: (paneelOpen) => set({ paneelOpen }),
  exporteer: () => JSON.stringify(get().scene, null, 2),
}))

/** Losse selectors — voorkomt dat elk 3D-component op de hele scene her-rendert. */
export const useLicht = () => useScene((s) => s.scene.licht)
export const useEffecten = () => useScene((s) => s.scene.effecten)
export const useMagazijnConfig = () => useScene((s) => s.scene.magazijn)
export const useObjecten = () => useScene((s) => s.scene.objecten)
export const useOppervlak = () => useScene((s) => s.scene.oppervlak)
export const useMaterialen = () => useScene((s) => s.scene.materialen)
export const useAnimatie = () => useScene((s) => s.scene.animatie)
export const useProfiel = () => useScene((s) => s.profiel)
